import type { StepConfiguration, UpholsteryCar } from "@views/Upholstery/types"

export const ROW_COUNT_SUBSTEP_KEY = "row-count"
export const ROW_COUNT_TWO = "2-row"
export const ROW_COUNT_THREE = "3-row"

/**
 * True when this vehicle's Rows step includes the 2-row / 3-row picker.
 */
export function vehicleNeedsRowCountChoice(
  vehicle?: Pick<UpholsteryCar, "steps"> | null
): boolean {
  const rowsStep = vehicle?.steps?.find((step) => step.key === "rows")
  return Boolean(
    rowsStep?.substeps?.some((substep) => substep.key === ROW_COUNT_SUBSTEP_KEY)
  )
}

export const ROW_COUNT_OPTIONS = [
  {
    id: "pdm-row-count-2",
    optionName: "2 Row",
    sku: "",
    optionValue: ROW_COUNT_TWO,
    optionImage: null,
  },
  {
    id: "pdm-row-count-3",
    optionName: "3 Row",
    sku: "",
    optionValue: ROW_COUNT_THREE,
    optionImage: null,
  },
] as const

/**
 * Which Rows substeps the UI may show.
 *
 * Matching vehicles (no picker): same as before — Front / Middle? / Back only.
 * Mismatch vehicles: How Many Rows is always listed; Front / Middle / Back
 * appear only after the user confirms 2 Row or 3 Row.
 */
export function isRowCountAwareRowsSubstepVisible(
  substepKey: string,
  opts: {
    needsRowCountChoice: boolean
    confirmed: boolean
    showMiddle: boolean
  }
): boolean {
  if (!opts.needsRowCountChoice) {
    if (substepKey === ROW_COUNT_SUBSTEP_KEY) return false
    if (substepKey === "middle") return opts.showMiddle
    return true
  }

  if (substepKey === ROW_COUNT_SUBSTEP_KEY) return true
  if (!opts.confirmed) return false
  if (substepKey === "middle") return opts.showMiddle
  return true
}

/**
 * When the user must pick 2-row vs 3-row, show middle only for 3-row.
 * Matching vehicles (no picker) keep the current middle-row behavior.
 */
export function shouldShowMiddleRow(config?: StepConfiguration): boolean {
  const choice = config?.rows?.substeps?.[ROW_COUNT_SUBSTEP_KEY]
  if (choice === undefined) return true
  return choice.value === ROW_COUNT_THREE
}

/**
 * 3-row when the user picked 3-row, or when there is no picker and middle exists.
 */
export function resolveRowCountFromRowsConfig(
  config?: StepConfiguration
): 2 | 3 {
  const choice = config?.rows?.substeps?.[ROW_COUNT_SUBSTEP_KEY]
  if (choice !== undefined) {
    return choice.value === ROW_COUNT_THREE ? 3 : 2
  }
  return config?.rows?.substeps?.middle ? 3 : 2
}

type RowSubstepConfig = {
  value?: string
  sku?: string
  label?: string
  image?: string
}

/**
 * Sidebar values for the Rows step.
 * On first load of a 2/3-row picker, hide the PDM default under How Many Rows
 * so the radios can look unselected. After the user picks, show the real label.
 * Matching vehicles (no picker) pass configs through unchanged.
 */
export function rowsSidebarStepConfigurations(
  substeps: Record<string, RowSubstepConfig> | undefined,
  needsRowCountChoice: boolean,
  confirmed: boolean
): Record<string, RowSubstepConfig> {
  const next = { ...(substeps ?? {}) }
  if (needsRowCountChoice && !confirmed) {
    delete next[ROW_COUNT_SUBSTEP_KEY]
  }
  return next
}
